import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';

export default function DashboardTour({ guide, onClose }) {
  const steps = useMemo(() => [
    { title: `Bienvenue sur ${guide.name}`, description: `Ce guide vous présente les principales zones de cette interface. Vous pourrez le relancer à tout moment depuis le menu de gauche.` },
    ...guide.steps,
  ], [guide]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const closeButtonRef = useRef(null);
  const step = steps[index];

  useEffect(() => {
    closeButtonRef.current?.focus();
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [index, onClose]);

  useEffect(() => {
    let scrolled = false;
    const measure = () => {
      const element = (step.selector && document.querySelector(step.selector))
        || (step.fallbackSelector && document.querySelector(step.fallbackSelector));
      if (!element) return setRect(null);
      if (!scrolled) {
        element.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        scrolled = true;
      }
      const bounds = element.getBoundingClientRect();
      setRect(bounds.width && bounds.height ? {
        top: Math.max(0, bounds.top - 6),
        left: Math.max(0, bounds.left - 6),
        width: bounds.width + 12,
        height: bounds.height + 12,
      } : null);
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const observer = new MutationObserver(measure);
    const content = document.querySelector('.dashboard-route-stage');
    if (content) observer.observe(content, { childList: true, subtree: true });
    const timer = window.setTimeout(measure, 350);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  const cardStyle = rect ? {
    top: rect.top + rect.height + 20 + 260 < window.innerHeight
      ? rect.top + rect.height + 16
      : Math.max(16, rect.top - 276),
    left: Math.max(16, Math.min(rect.left, window.innerWidth - 376)),
  } : undefined;

  return createPortal(
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-labelledby="dashboard-tour-title" aria-describedby="dashboard-tour-description">
      {rect ? (
        <>
          <div className="fixed inset-0 bg-transparent" onClick={onClose} />
          <div className="fixed pointer-events-none rounded-2xl ring-4 ring-[#B69AD0] shadow-[0_0_0_9999px_rgba(18,12,28,0.68)]" style={rect} aria-hidden="true" />
        </>
      ) : <div className="fixed inset-0 bg-[#120c1c]/70" onClick={onClose} />}
      <div className={`fixed w-[min(360px,calc(100vw-32px))] rounded-2xl bg-white p-6 text-slate-900 shadow-2xl ${rect ? '' : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'}`} style={cardStyle}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6B4C9A]">Guide de l’interface · {index + 1}/{steps.length}</span>
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Fermer le guide" className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#6B4C9A]"><X size={20} /></button>
        </div>
        <h2 id="dashboard-tour-title" className="mb-2 text-xl font-bold">{step.title}</h2>
        <p id="dashboard-tour-description" className="text-sm leading-6 text-slate-600">{step.description}</p>
        <div className="mt-6 flex items-center justify-between gap-3">
          <button type="button" onClick={() => setIndex(value => value - 1)} disabled={index === 0} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-[#523467] disabled:invisible focus:outline-none focus:ring-2 focus:ring-[#6B4C9A]"><ArrowLeft size={16} />Précédent</button>
          {index === steps.length - 1 ? (
            <button type="button" onClick={onClose} className="rounded-lg bg-[#6B4C9A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#59407F] focus:outline-none focus:ring-2 focus:ring-[#6B4C9A] focus:ring-offset-2">Terminer</button>
          ) : (
            <button type="button" onClick={() => setIndex(value => value + 1)} className="inline-flex items-center gap-1 rounded-lg bg-[#6B4C9A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#59407F] focus:outline-none focus:ring-2 focus:ring-[#6B4C9A] focus:ring-offset-2">Suivant<ArrowRight size={16} /></button>
          )}
        </div>
      </div>
    </div>, document.body
  );
}
